<template>
  <div class="relative flex flex-col">
    <slot :clear="clearSelection" :toggle="toggleSelector" />
    <div
      v-if="showMonthSelector"
      class="w-72 bg-white p-2"
      :class="{
        'absolute top-11 z-50 border border-gray-200 shadow-md': !embedded,
        'right-0': position === 'right' && !embedded,
        'left-0': position === 'left' && !embedded,
      }"
    >
      <div class="mb-2 flex w-full">
        <button
          class="h-10 w-12 bg-gray-100 pr-1 pb-1"
          role="decrease-year"
          @click.prevent="setYear(year - 1)"
        >
          <icon-base
            height="10"
            width="10"
            :icon="IconArrowUp"
            opacity="0.5"
            class="rotate-90 transform text-gray-500"
          />
        </button>
        <div
          class="mx-2 w-full rounded-4 border border-gray-200 py-2 text-center font-bold text-gray-700"
        >
          {{ year }}
        </div>
        <button
          class="h-10 w-12 bg-gray-100 pb-1 pl-1"
          role="increase-year"
          @click.prevent="setYear(year + 1)"
        >
          <icon-base
            height="10"
            width="10"
            :icon="IconArrowUp"
            opacity="0.5"
            class="-rotate-90 transform text-gray-500"
          />
        </button>
      </div>
      <div class="grid grid-cols-6 gap-2">
        <div
          v-for="month in MONTHS"
          :key="month.monthNum"
          class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-4 border border-gray-200 text-xs font-semibold uppercase"
          :class="[
            {
              'bg-primary text-white': isMonthInSelectedRange(month.monthNum),
            },
            { 'text-disabled-medium': !isMonthInSelectedRange(month.monthNum) },
          ]"
          @click="selectDate(month.monthNum)"
        >
          {{ month.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref, watch } from "vue";

import { MONTHS } from "@/helpers/constants";
import { formatDateCustom } from "@/helpers/formatting";

import IconArrowUp from "@/components/icons/IconArrowUp.vue";

import type { PropType } from "vue";

const props = defineProps({
  modelValue: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  dateFormat: {
    type: String,
    default: "MM/dd/yyyy",
  },
  showSelector: {
    type: Boolean,
    default: false,
  },
  embedded: {
    type: Boolean,
    default: false,
  },
  isDateRange: {
    type: Boolean,
    default: true,
  },
  position: {
    type: String,
    default: "right",
  },
});

const currentDate = new Date();
const showMonthSelector = ref(props.showSelector);

const toggleSelector = () => {
  showMonthSelector.value = !showMonthSelector.value;
};

const emit = defineEmits(["update:modelValue"]);

const year = ref(currentDate.getFullYear());

const setYear = (val: number) => {
  year.value = val;
};
interface DateObject {
  day: number;
  month: number | null;
  year: number | null;
}

const dateRange = reactive<{ start: DateObject; end: DateObject }>({
  start: {
    day: 1,
    month: null,
    year: null,
  },
  end: {
    day: 1,
    month: null,
    year: null,
  },
});

const selectDate = (monthIndx: number) => {
  if (!props.isDateRange) {
    dateRange.start.month = monthIndx;
    dateRange.start.year = year.value;
    emit("update:modelValue", [
      formatDateCustom(
        `${dateRange.start.month}/${dateRange.start.day}/${dateRange.start.year}`,
        props.dateFormat,
        true
      ),
    ]);
  }
  if (dateRange.start.month === null) {
    dateRange.start.month = monthIndx;
    dateRange.start.year = year.value;
    return;
  }

  if (dateRange.end.month === null && props.isDateRange) {
    dateRange.end.month = monthIndx;
    dateRange.end.year = year.value;
    if (
      new Date(
        `${dateRange.start.month}/${dateRange.start.day}/${dateRange.start.year}`
      ) >
      new Date(
        `${dateRange.end.month}/${dateRange.end.day}/${dateRange.end.year}`
      )
    ) {
      const tempMonth = dateRange.start.month;
      const tempYear = dateRange.start.year;
      dateRange.start.month = dateRange.end.month;
      dateRange.end.month = tempMonth;
      dateRange.start.year = dateRange.end.year;
      dateRange.end.year = tempYear;
    }
    const lastDayOfMonth = new Date(year.value, dateRange.end.month, 0);
    dateRange.end.day = lastDayOfMonth.getDate();
    emit("update:modelValue", [
      formatDateCustom(
        `${dateRange.start.month}/${dateRange.start.day}/${dateRange.start.year}`,
        props.dateFormat,
        true
      ),
      formatDateCustom(
        `${dateRange.end.month}/${dateRange.end.day}/${dateRange.end.year}`,
        props.dateFormat,
        true
      ),
    ]);
    if (!props.showSelector) {
      toggleSelector();
    }
    return;
  }

  dateRange.start.month = monthIndx;
  dateRange.start.year = year.value;
  dateRange.end.month = null;
  dateRange.end.year = null;
};

const clearSelection = () => {
  dateRange.start.month = null;
  dateRange.start.year = null;
  dateRange.end.month = null;
  dateRange.end.year = null;
  year.value = currentDate.getFullYear();
  emit("update:modelValue", []);
};

const isMonthSelected = (index: number) =>
  (dateRange.start.month === index && year.value === dateRange.start.year) ||
  (dateRange.end.month === index && year.value === dateRange.end.year);

const isMonthInSelectedRange = (index: number) => {
  if (props.isDateRange) {
    const hasEndDateAndStartDate = dateRange.start.month && dateRange.end.month;
    const isBetweenStartDateAndEndDate =
      !dateRange.start.month || !dateRange.end.month
        ? false
        : index >= dateRange.start.month && index <= dateRange.end.month;
    const isTheSameYear =
      year.value === dateRange.start.year && year.value === dateRange.end.year;
    return (
      isMonthSelected(index) ||
      (hasEndDateAndStartDate && isBetweenStartDateAndEndDate && isTheSameYear)
    );
  }
  return isMonthSelected(index);
};

watch(
  () => props.showSelector,
  () => {
    showMonthSelector.value = props.showSelector;
  }
);

const updateDateRange = (value: string[]) => {
  let startDate = null;
  let endDate = null;
  if (value[0]) {
    startDate = new Date(formatDateCustom(value[0]));
    dateRange.start.month = startDate.getMonth() + 1;
    dateRange.start.year = startDate.getFullYear();
  }

  if (value[1]) {
    endDate = new Date(formatDateCustom(value[1]));
    dateRange.end.month = endDate.getMonth() + 1;
    dateRange.end.year = endDate.getFullYear();

    year.value = dateRange.end.year;
  }
};

watch(() => props.modelValue, updateDateRange);

onMounted(() => {
  if (props.modelValue?.length) {
    updateDateRange(props.modelValue);
  }
});
</script>
